import React from 'react';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import ImageOutlineIcon from 'mdi-react/ImageOutlineIcon';
import CalendarMonthIcon from 'mdi-react/CalendarMonthIcon';
import { useGetFacilityServices } from '../../../App/customize/hooks/useGetFacilityServices';

const VET_UPDATES_HEADER_TOOLTIP = (
  <>
    <div className="lp-info-tooltip__section">
      <div className="lp-info-tooltip__heading-row">
        <ImageOutlineIcon
          size={16}
          className="lp-info-tooltip__heading-icon lp-info-tooltip__heading-icon--photo"
          aria-hidden
        />
        <span className="lp-info-tooltip__heading-text">Photos in Updates</span>
      </div>
      <div className="lp-info-tooltip__body">
        <strong>
          The number shows how many photos were included in updates:
        </strong>
        <br />
        (i) taken and sent from the iPad app and/or
        <br />
        (ii) attached to updates sent from the Web app.
      </div>
    </div>
    <div className="lp-info-tooltip__section lp-info-tooltip__section--after-photos">
      <div className="lp-info-tooltip__heading-row">
        <ContentCopyIcon
          size={16}
          className="lp-info-tooltip__heading-icon"
          aria-hidden
        />
        <span className="lp-info-tooltip__heading-text">
          Copy Update History
        </span>
      </div>
      <div className="lp-info-tooltip__body">
        For items with a <em>View history</em> link, click the copy icon to copy
        the history of sent updates. You can then paste the history elsewhere.
        (No history is available to copy for items showing{' '}
        <em>No updates sent</em>.)
      </div>
    </div>
  </>
);

const BOARDING_UPDATES_HEADER_TOOLTIP = (
  <>
    <div className="lp-info-tooltip__section">
      <div className="lp-info-tooltip__heading-row">
        <ImageOutlineIcon
          size={16}
          className="lp-info-tooltip__heading-icon lp-info-tooltip__heading-icon--photo"
          aria-hidden
        />
        <span className="lp-info-tooltip__heading-text">Photos in Updates</span>
      </div>
      <div className="lp-info-tooltip__body">
        <strong>
          The number shows how many photos were included in updates:
        </strong>
        <br />
        (i) taken and sent from the iPad app and/or
        <br />
        (ii) attached to updates sent from the Web app.
      </div>
    </div>
    <div className="lp-info-tooltip__section lp-info-tooltip__section--after-photos">
      <div className="lp-info-tooltip__heading-row">
        <CalendarMonthIcon
          size={16}
          className="lp-info-tooltip__heading-icon lp-info-tooltip__heading-icon--calendar"
          aria-hidden
        />
        <span className="lp-info-tooltip__heading-text">Days of Stay</span>
      </div>
      <div className="lp-info-tooltip__body">
        <strong>The number shows the calendar days so far for:</strong>
        <br />
        (i) an <em>ongoing</em> Boarding stay or
        <br />
        (ii) the total calendar days for a <em>past</em> Boarding stay.
      </div>
    </div>
  </>
);

/**
 * Profile Update History / Photo Update History column configs.
 * Mirrors main Update History column order, labels, keys, and classNames
 * (minus Parent Name). Sorting via profile Sort control.
 *
 * @param {{ futureView?: boolean }} options
 */
export function useParentProfileAppointmentsHeaders({ futureView = false } = {}) {
  const { data: services } = useGetFacilityServices();
  const isBoardingOnly = !!services?.isBoardingOnly;

  const allHeaders = [
    {
      key: 'dateTime',
      name: 'Date',
      sortable: false,
      filterable: false,
      capitalize: true,
      className: 'date-column',
    },
    {
      key: 'endDate',
      name: 'End Date',
      sortable: false,
      filterable: false,
      capitalize: false,
      className: 'date-column',
    },
    {
      key: 'time',
      name: 'Time',
      sortable: false,
      filterable: false,
      capitalize: true,
      className: 'time-column',
    },
    {
      key: 'status',
      name: futureView ? 'Status' : 'Update History',
      sortable: false,
      filterable: false,
      capitalize: true,
      className: futureView ? 'appt-status-column' : 'appt-updates-column',
      ...(!futureView
        ? {
            headerTooltip: isBoardingOnly
              ? BOARDING_UPDATES_HEADER_TOOLTIP
              : VET_UPDATES_HEADER_TOOLTIP,
            headerTooltipClassName: 'lp-info-tooltip',
          }
        : {}),
    },
    {
      key: 'typeTitle',
      name: 'Type',
      sortable: false,
      filterable: false,
      capitalize: true,
      className: 'appt-type-column',
    },
    {
      key: 'petName',
      name: 'Pet Name',
      sortable: false,
      filterable: false,
      capitalize: true,
      className: 'appt-pet-column',
    },
    {
      key: 'doctor',
      name: 'Doctor',
      sortable: false,
      filterable: false,
      capitalize: true,
      className: 'appt-doctor-column',
    },
  ];

  return allHeaders.filter((h) => {
    if (h.key === 'endDate' && !(isBoardingOnly && !futureView)) {
      return false;
    }
    if (h.key === 'doctor' && isBoardingOnly) {
      return false;
    }
    if (h.key === 'time' && (isBoardingOnly || !futureView)) {
      return false;
    }
    return true;
  });
}
